import { lentComponent, useContractMutation, useContractQuery, useInvalidateContract } from "@amtana/browser";
import type { LentToken, MutationResult, QueryResult } from "@amtana/browser";
import { exportTrpcContract } from "@amtana/export-contract";
import type { CreateExportInput, Export, ExportFile, ExportSource } from "@amtana/export-contract";

// Exports for every browser module, and the export dialog export lends to any
// screen that shows prospects.

export type ExportDialogProps = {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly source: ExportSource;
  readonly scope: CreateExportInput["scope"];
};

export const ExportDialog: LentToken<ExportDialogProps> = lentComponent<ExportDialogProps>(
  "export",
  "dialog",
);

/** What a person leaving can take from the gate (ADR-032, S37.07): the exports they already made. */
export const ExportsToTake: LentToken<Record<string, never>> = lentComponent<Record<string, never>>(
  "export",
  "to-take",
);

export function useExports(
  options: { live?: boolean; optional?: boolean } = {},
): QueryResult<{ exports: Export[] }> {
  return useContractQuery(exportTrpcContract, "list", {
    input: {},
    refetchInterval: options.live ? 1500 : false,
    optional: options.optional ?? false,
  });
}

export function useExport(exportId: string, options: { live?: boolean } = {}): QueryResult<Export> {
  return useContractQuery(exportTrpcContract, "get", {
    input: { exportId },
    refetchInterval: options.live ? 1500 : false,
  });
}

export function useExportFile(exportId: string, options: { enabled: boolean }): QueryResult<ExportFile> {
  return useContractQuery(exportTrpcContract, "file", { input: { exportId }, enabled: options.enabled });
}

export function useCreateExport(): MutationResult<Export, CreateExportInput> {
  const mutation = useContractMutation(exportTrpcContract, "create");
  const invalidate = useInvalidateContract();
  return {
    ...mutation,
    run: (input: CreateExportInput) =>
      mutation.mutateAsync(input).finally(() => void invalidate(exportTrpcContract)),
  };
}

export function useRetryExport(): MutationResult<Export, { exportId: string }> {
  const mutation = useContractMutation(exportTrpcContract, "retry");
  const invalidate = useInvalidateContract();
  return {
    ...mutation,
    run: (input: { exportId: string }) =>
      mutation.mutateAsync(input).finally(() => void invalidate(exportTrpcContract)),
  };
}
